import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SlashCommand } from '@cyphex/types/platform';
import { useCommands } from './useCommandRegistry';

/** Never show more than this — the popover must not cover the conversation. */
const MAX_SUGGESTIONS = 8;

export interface SlashCommandState {
  /** The commands to render, already filtered and ranked. Empty means hide. */
  suggestions: SlashCommand[];
  /** Index of the highlighted row. Always valid when `suggestions` is non-empty. */
  activeIndex: number;
  /** True when the popover is showing and should own the arrow/enter keys. */
  isOpen: boolean;
  move: (delta: number) => void;
  /** The text the composer should hold after accepting the highlighted row. */
  accept: (command?: SlashCommand) => string | null;
  close: () => void;
}

/**
 * Slash-command autocomplete for the composer.
 *
 * TRIGGERS ONLY ON A LEADING SLASH. `text.startsWith('/')` and nothing else:
 * offering completions for a slash mid-sentence would fire on every URL anybody
 * pastes, and "https://" would open a command menu.
 *
 * IT CLOSES THE MOMENT A SPACE IS TYPED. Once the composer reads `/deploy `, the
 * command is chosen and the person is writing ARGUMENTS. Keeping the menu open
 * would leave it swallowing Enter while they type, which is the specific way
 * this feature becomes infuriating rather than helpful.
 *
 * Ranking is prefix-first: someone typing `/de` means `/deploy`, not
 * `/incident-declare`, even though both contain "de".
 */
export function useSlashCommands(text: string, enabled: boolean): SlashCommandState {
  const { data: commands } = useCommands();
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const query = useMemo(() => {
    if (!enabled || !text.startsWith('/')) return null;
    const rest = text.slice(1);
    // A space means the command word is complete — see the note above.
    if (/\s/.test(rest)) return null;
    return rest.toLowerCase();
  }, [enabled, text]);

  const suggestions = useMemo(() => {
    if (query === null || dismissed) return [];
    const all = commands ?? [];
    if (query === '') return all.slice(0, MAX_SUGGESTIONS);

    const prefix: SlashCommand[] = [];
    const contains: SlashCommand[] = [];
    for (const command of all) {
      const name = command.command.toLowerCase();
      if (name.startsWith(query)) prefix.push(command);
      else if (name.includes(query) || command.description.toLowerCase().includes(query)) {
        contains.push(command);
      }
    }
    return [...prefix, ...contains].slice(0, MAX_SUGGESTIONS);
  }, [commands, dismissed, query]);

  // Re-typing must put the highlight back on the best match. Without this, a
  // stale index silently selects the wrong command after the list narrows.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Leaving slash-mode entirely re-arms the menu, so Escape dismisses THIS
  // attempt rather than disabling the feature until the composer remounts.
  useEffect(() => {
    if (query === null) setDismissed(false);
  }, [query]);

  const move = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        const count = suggestions.length;
        if (count === 0) return 0;
        return (current + delta + count) % count;
      });
    },
    [suggestions.length],
  );

  const accept = useCallback(
    (command?: SlashCommand): string | null => {
      const chosen = command ?? suggestions[activeIndex];
      if (chosen === undefined) return null;
      // A trailing space both closes the menu (the query goes null) and puts the
      // caret where arguments go. One keystroke saved on every invocation.
      return `/${chosen.command} `;
    },
    [activeIndex, suggestions],
  );

  const close = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    suggestions,
    activeIndex,
    isOpen: suggestions.length > 0,
    move,
    accept,
    close,
  };
}
